import { useState, useEffect } from "react"
import { getWorkCategories, createWorkCategory } from "api/apiHelper"
import { WorkCategory } from "types/employee-utilization"
import { toast } from "react-toastify"
import Backdrop from "components/Backdrop"
import ErrorBox from "components/ErrorBox"

const WorkCategoryManagement = () => {
  const [categories, setCategories] = useState<WorkCategory[]>([])
  const [newCategory, setNewCategory] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch existing categories
  const fetchCategories = async () => {
    try {
      setIsLoading(true)
      const data = await getWorkCategories()
      setCategories(data)
    } catch (err: any) {
      setError(err.message || "Failed to fetch categories")
      toast.error("Failed to fetch work categories")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchCategories()
  }, [])

  // Handle category creation
  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newCategory.trim()) {
      toast.error("Category name cannot be empty")
      return
    }

    setIsLoading(true)
    try {
      const created = await createWorkCategory({ type: newCategory.trim() })
      setCategories([...categories, created])
      setNewCategory("")
      toast.success("Work category created successfully")
    } catch (err: any) {
      setError(err.message || "Failed to create category")
      toast.error("Failed to create work category")
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return <Backdrop show={isLoading} />
  }

  if (error) {
    return <ErrorBox errorMessage={error} />
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="bg-white dark:bg-navy-800 rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-semibold text-navy-700 dark:text-white mb-6">
          Work Categories Management
        </h2>

        {/* Create Category Form */}
        <form onSubmit={handleCreateCategory} className="mb-8">
          <div className="flex gap-4">
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Enter new category name"
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-navy-600 rounded-lg bg-white dark:bg-navy-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            />
            <button
              type="submit"
              disabled={isLoading || !newCategory.trim()}
              className="px-6 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium transition-all"
            >
              Add Category
            </button>
          </div>
        </form>

        {/* Categories List */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Existing Categories
          </h3>
          
          {categories.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400">
              No work categories found. Create one above.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="p-4 border border-gray-200 dark:border-navy-600 rounded-lg bg-gray-50 dark:bg-navy-900"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-900 dark:text-white font-medium">
                      {category.type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default WorkCategoryManagement